import './style.css';
import { AppStateMachine } from './core/stateMachine.js';
import { getSeasonProfile } from './core/seasons.js';
import {
  AppState,
  FpsTarget,
  InputEventType,
  Seasons
} from './core/constants.js';
import { validateUpload } from './core/validation.js';
import { PerformanceMonitor } from './core/perfMonitor.js';
import { SceneController } from './three/sceneController.js';
import { KeyboardController } from './input/keyboardController.js';
import { GestureController } from './input/gestureController.js';
import { HudController } from './ui/hud.js';

const sceneRoot = document.querySelector('#scene-root');
const fileInput = document.querySelector('#file-input');
const gestureBtn = document.querySelector('#gesture-btn');
const gestureVideo = document.querySelector('#gesture-video');

const hud = new HudController();
const stateMachine = new AppStateMachine(AppState.CLOSED);
const performanceMonitor = new PerformanceMonitor(300);
const sceneController = new SceneController(sceneRoot);

let seasonProfile = getSeasonProfile(Seasons.SPRING);
let gestureController = null;
let gestureEnabled = false;
let warningThrottleAt = 0;
let lastPerfUiAt = 0;
let elapsedSeconds = 0;
let lastFrameAt = performance.now();

function syncSeasonToUi() {
  hud.setSeasonLabel(seasonProfile.label);
  document.documentElement.style.setProperty('--season-bg', seasonProfile.background);
}

function syncStateToUi() {
  hud.setState(stateMachine.state);
}

function applySeason(seasonId) {
  seasonProfile = getSeasonProfile(seasonId);
  sceneController.applySeason(seasonProfile);
  syncSeasonToUi();
  hud.toast(`切换到 ${seasonProfile.label}`, 'success');
}

function dispatchStateEvent(type, payload = {}) {
  const result = stateMachine.dispatch(type, payload);
  if (!result.ok) {
    return false;
  }
  syncStateToUi();
  return true;
}

function handleKeyboardAction(action) {
  if (action.type === 'SEASON_KEY') {
    applySeason(action.season);
    return;
  }

  if (action.type === 'TOGGLE_STATE') {
    if (stateMachine.state === AppState.CLOSED) {
      dispatchStateEvent(InputEventType.GO_DISPERSED);
    } else {
      dispatchStateEvent(InputEventType.GO_CLOSED);
    }
    return;
  }

  if (action.type === 'EXIT_FOCUS') {
    dispatchStateEvent(InputEventType.EXIT_PHOTO_FOCUS);
  }
}

function normalizeToNdc(x, y) {
  return {
    x: x * 2 - 1,
    y: -(y * 2 - 1)
  };
}

function handleGesture(event) {
  if (event.type === 'FIST') {
    dispatchStateEvent(InputEventType.GO_CLOSED);
    return;
  }

  if (event.type === 'OPEN_PALM') {
    dispatchStateEvent(InputEventType.GO_DISPERSED);
    return;
  }

  if (event.type === 'ROTATE' && stateMachine.state === AppState.DISPERSED) {
    sceneController.applyRotateDelta(event.deltaX, event.deltaY);
    return;
  }

  if (
    (event.type === 'PINCH_START' || event.type === 'PINCH_MOVE') &&
    (stateMachine.state === AppState.DISPERSED || stateMachine.state === AppState.PHOTO_FOCUS) &&
    sceneController.photoCount() > 0
  ) {
    const pointer = normalizeToNdc(event.x, event.y);
    const picked = sceneController.pickPhotoByNdc(pointer.x, pointer.y);
    if (Number.isInteger(picked)) {
      dispatchStateEvent(InputEventType.ENTER_PHOTO_FOCUS, { photoIndex: picked });
    }
  }
}

function onGestureStatus(status) {
  if (status.type === 'GESTURE_READY') {
    hud.setGestureStatus('手势已启用');
    hud.toast('摄像头已接入，手势控制可用。', 'success');
    return;
  }

  if (status.type === 'GESTURE_ERROR') {
    hud.setGestureStatus('手势不可用，已切换为键盘模式', true);
    hud.toast(status.message, 'error');
    gestureEnabled = false;
    gestureBtn.textContent = '重试手势';
  }
}

async function toggleGesture() {
  if (gestureEnabled && gestureController) {
    gestureController.stop();
    gestureEnabled = false;
    gestureBtn.textContent = '启用手势';
    hud.setGestureStatus('手势已关闭（可直接用键盘）');
    return;
  }

  gestureController = new GestureController({
    videoEl: gestureVideo,
    onGesture: handleGesture,
    onStatus: onGestureStatus
  });

  hud.setGestureStatus('正在初始化手势...');
  gestureBtn.textContent = '初始化中...';
  await gestureController.start();

  if (gestureController.running) {
    gestureEnabled = true;
    gestureBtn.textContent = '关闭手势';
  }
}

async function handleUploadChange(event) {
  const validation = validateUpload(event.target.files);
  if (!validation.ok) {
    hud.toast(validation.errors[0].message, 'error');
    fileInput.value = '';
    return;
  }

  try {
    await sceneController.loadPhotos(validation.acceptedFiles);
    hud.setPhotoCount(sceneController.photoCount());
    hud.toast(`上传成功：${sceneController.photoCount()} 张照片`, 'success');
  } catch (error) {
    hud.toast(`图片加载失败：${error.message || '请重试'}`, 'error');
  } finally {
    fileInput.value = '';
  }
}

function animate(now) {
  const deltaSeconds = Math.min((now - lastFrameAt) / 1000, 0.05);
  lastFrameAt = now;
  elapsedSeconds += deltaSeconds;

  const snapshot = stateMachine.snapshot();
  sceneController.update({
    ...snapshot,
    seasonProfile,
    elapsedSeconds,
    deltaSeconds
  });
  sceneController.render();

  performanceMonitor.pushFrame(deltaSeconds * 1000);

  if (now - lastPerfUiAt > 1000) {
    const perfSnapshot = performanceMonitor.snapshot();
    hud.setPerformance(perfSnapshot);

    if (
      perfSnapshot.sampleCount > 80 &&
      perfSnapshot.averageFps < FpsTarget.WARNING &&
      now - warningThrottleAt > 8000
    ) {
      warningThrottleAt = now;
      hud.toast(
        `当前平均帧率 ${perfSnapshot.averageFps.toFixed(1)}，低于目标 60FPS，建议减少照片数量。`,
        'warn'
      );
      console.warn(
        `[performance] average=${perfSnapshot.averageFps.toFixed(2)}fps, 1%Low=${perfSnapshot.onePercentLow.toFixed(2)}fps`
      );
    }
    lastPerfUiAt = now;
  }

  requestAnimationFrame(animate);
}

function bootstrap() {
  applySeason(Seasons.SPRING);
  syncStateToUi();
  hud.setPhotoCount(0);
  hud.setGestureStatus('手势未启用（可直接用键盘）');

  new KeyboardController({ onAction: handleKeyboardAction });

  fileInput.addEventListener('change', handleUploadChange);
  gestureBtn.addEventListener('click', toggleGesture);

  window.addEventListener('resize', () => sceneController.resize());
  requestAnimationFrame(animate);
}

bootstrap();

window.addEventListener('beforeunload', () => {
  if (gestureController) {
    gestureController.stop();
  }
  sceneController.dispose();
});
